import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material'
import {
  Save as SaveIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material'
import Editor from '@monaco-editor/react'

function YamlEditor({ yaml, onSave, onValidate, validationResult }) {
  const [editorValue, setEditorValue] = useState(yaml || '')
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)
  const [validating, setValidating] = useState(false)
  const [showValidation, setShowValidation] = useState(true)
  const [saveSuccess, setSaveSuccess] = useState(false)

  useEffect(() => {
    setEditorValue(yaml || '')
    setHasChanges(false)
  }, [yaml])

  const handleEditorChange = useCallback((value) => {
    setEditorValue(value || '')
    setHasChanges(value !== yaml)
    setSaveSuccess(false)
  }, [yaml])

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(editorValue)
      setHasChanges(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (error) {
      console.error('Save failed:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleValidate = async () => {
    setValidating(true)
    try {
      await onValidate()
      setShowValidation(true)
    } finally {
      setValidating(false)
    }
  }

  const renderValidationResult = () => {
    if (!validationResult) return null

    const { valid, errors, warnings, stats } = validationResult

    return (
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setShowValidation(!showValidation)}
        >
          {valid ? (
            <Chip icon={<CheckIcon />} label="Valid" color="success" size="small" />
          ) : (
            <Chip icon={<ErrorIcon />} label="Invalid" color="error" size="small" />
          )}
          {warnings?.length > 0 && (
            <Chip icon={<WarningIcon />} label={`${warnings.length} warnings`} color="warning" size="small" />
          )}
          {stats && (
            <>
              <Chip label={`${stats.chapter_count} chapters`} size="small" variant="outlined" />
              <Chip label={`${stats.lesson_count} lessons`} size="small" variant="outlined" />
            </>
          )}
          {showValidation ? <CollapseIcon /> : <ExpandIcon />}
        </Box>

        <Collapse in={showValidation}>
          {errors?.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 1, p: 1, bgcolor: 'error.lighter' }}>
              <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                Errors
              </Typography>
              <List dense disablePadding>
                {errors.map((err, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ErrorIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={err.message}
                      secondary={err.field}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', fontFamily: 'monospace' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {warnings?.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 1, p: 1, bgcolor: 'warning.lighter' }}>
              <Typography variant="subtitle2" color="warning.dark" sx={{ mb: 1 }}>
                Warnings
              </Typography>
              <List dense disablePadding>
                {warnings.map((warn, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <WarningIcon fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={warn.message}
                      secondary={warn.field}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', fontFamily: 'monospace' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Collapse>
      </Box>
    )
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {hasChanges && (
            <Chip label="Unsaved changes" size="small" color="warning" variant="outlined" />
          )}
          {saveSuccess && (
            <Chip icon={<CheckIcon />} label="Saved" size="small" color="success" />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleValidate}
            disabled={validating || !editorValue}
            startIcon={validating ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Validate
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            Save
          </Button>
        </Box>
      </Box>

      {/* Monaco Editor */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Editor
          height="500px"
          language="yaml"
          value={editorValue}
          onChange={handleEditorChange}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            tabSize: 2,
            automaticLayout: true,
          }}
          theme="vs-light"
        />
      </Paper>

      {/* Validation Results */}
      {renderValidationResult()}

      {/* Help Text */}
      <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>Tips:</strong> Each chapter should have a unique chapter_id and lessons array.
          Lesson types can be: lesson, exercise, quiz, or summary.
          For exercises, also specify an exercise_type (breathing, meditation, journaling, etc.).
        </Typography>
      </Alert>
    </Box>
  )
}

export default YamlEditor
